import i18n from 'simple-react-i18n'
import { D101_0, D102_1, D151_0, FIELD_TYPE_FLOAT, FIELD_TYPE_INT, FINANCIAL, INCOMPLETE, NETWORK, QUALITY, SUBSCRIBER, IN_PROGRESS, P101_1, P102_1, P103_2B, P104_3, P105_3, P106_3, P107_2, P108_3, P109_0, P151_1, P152_1, P153_2, P154_0, P155_1_RATE, PENDING_VALIDATION, RPQS_STATUSES, VALIDATED } from 'rpqs/constants/RpqsConstant'
import { fromPairs, isNil, omit } from 'lodash'

// Statut back (entier) -> constante d'affichage front (0 ou absent = incomplet).
const STATUS_BY_CODE = {
    0: INCOMPLETE,
    1: IN_PROGRESS,
    2: PENDING_VALIDATION,
    3: VALIDATED,
}

export const getStatusFromCode = code => STATUS_BY_CODE[code] ?? INCOMPLETE

const THOUSANDS_SEPARATOR = ' '

// '-1234567,5' -> '-1 234 567,5' (espace insécable). Une valeur non numérique est renvoyée telle quelle.
export const formatThousands = value => {
    const str = `${value ?? ''}`
    const match = str.match(/^(-?)(\d+)([.,]\d*)?$/)
    if (!match) return str
    const [, sign, integerPart, decimalPart = ''] = match
    return `${sign}${integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, THOUSANDS_SEPARATOR)}${decimalPart}`
}

export const formatIndicatorValue = (value, fieldType) => (fieldType === FIELD_TYPE_INT || fieldType === FIELD_TYPE_FLOAT ? formatThousands(value) : value)

export const removeThousandsSeparators = value => `${value ?? ''}`.replace(/\s/g, '')

export const isRpqsLoaded = (rpqs, year, collectivityId) => rpqs?.year === parseInt(year) && rpqs?.collectivityId === parseInt(collectivityId)

// Arborescence FTP : DOCUMENTS/RPQS/{année}/{collectivité}/ pour le RPQS global,
// DOCUMENTS/RPQS/{année}/{collectivité}/{EG}/ pour une entité de gestion.
export const RPQS_DOCUMENTS_ROOT = 'DOCUMENTS/RPQS/'

export const getRpqsCollectivityDocumentsPath = (year, collectivityId) => `${RPQS_DOCUMENTS_ROOT}${year}/${collectivityId}/`

export const getRpqsDocumentsPath = (year, collectivityId, egId) => {
    const collectivityPath = getRpqsCollectivityDocumentsPath(year, collectivityId)
    return isNil(egId) ? collectivityPath : `${collectivityPath}${egId}/`
}

export const CATEGORY_CODES = {
    [SUBSCRIBER]: 1,
    [NETWORK]: 2,
    [QUALITY]: 3,
    [FINANCIAL]: 4,
}

const isMandatoryIndicator = indicator => !isNil(indicator.mandatory) && indicator.mandatory.toLowerCase() !== 'false'

export const countIndicators = (indicators = [], predicate = () => true) => {
    const statusByMainCode = fromPairs(indicators.filter(ind => !ind.parent).map(ind => [ind.code, ind.status]))
    const required = indicators.filter(ind => predicate(ind) && isMandatoryIndicator(ind))
    const filled = required.filter(ind => `${ind.value ?? ''}`.trim() !== '' && (statusByMainCode[ind.parent || ind.code] ?? 0) !== 0)
    return { filled: filled.length, required: required.length }
}

export const countGroupIndicators = (indicators, mainCode) => countIndicators(indicators, ind => ind.code === mainCode || ind.parent === mainCode)

export const sumIndicatorCounts = (counts = []) => counts.reduce((acc, c) => ({ filled: acc.filled + c.filled, required: acc.required + c.required }), { filled: 0, required: 0 })

export const getCountPercent = ({ filled, required }) => (required ? Math.round((filled * 100) / required) : 0)

export const formatIndicatorCount = ({ filled, required }) => `${filled} / ${required}`

export const getRpqsStatus = code => RPQS_STATUSES[code] ?? RPQS_STATUSES[0]

export const getGlobalRpqsStatus = (egStatuses = []) => (egStatuses.length ? Math.min(...egStatuses.map(s => s.status)) : 0)

export const getRpqsStatusBadges = (egStatuses = []) => RPQS_STATUSES.map(({ code }) => egStatuses.filter(s => s.status === code).length)

export const getRpqsStatusDates = (egStatuses = []) => RPQS_STATUSES.map(({ dateField }) => {
    const dates = egStatuses.map(s => s[dateField])
    return dates.length && dates.every(d => !isNil(d)) ? Math.max(...dates) : undefined
})

export const canGoToNextRpqsStatus = (egStatuses = []) => {
    const statuses = [...new Set(egStatuses.map(s => s.status))]
    return statuses.length === 1 && statuses[0] >= 2 && statuses[0] < RPQS_STATUSES.length - 1
}

export const sortCollectivities = (a, b, descending=false) => {
    if (a === i18n.unknown) return 1
    if (b === i18n.unknown) return -1
    if (descending) return b.toLowerCase().localeCompare(a.toLowerCase())
    return a.toLowerCase().localeCompare(b.toLowerCase())
}

export const getSubscriberIndicators = () => {
    return [
        {
            indicatorDenomination: D102_1.i18n,
            indicator: D102_1.name,
            code: D102_1.code,
            unit: i18n.currentEuros,
        },
        {
            indicatorDenomination: D101_0.i18n,
            indicator: D101_0.name,
            code: D101_0.code,
            unit: i18n.nbPeopleServed,
        },
        {
            indicatorDenomination: P155_1_RATE.i18n,
            indicator: P155_1_RATE.name,
            code: P155_1_RATE.code,
            unit: '%',
        },
        {
            indicatorDenomination: P152_1.i18n,
            indicator: P152_1.name,
            code: P152_1.code,
            unit: '%',
        },
        {
            indicatorDenomination: D151_0.i18n,
            indicator: D151_0.name,
            code: D151_0.code,
            unit: i18n.hourOrDay,
        },
        {
            indicatorDenomination: P151_1.i18n,
            indicator: P151_1.name,
            code: P151_1.code,
            unit: i18n.subscribersUnit,
        },
    ]
}

export const getNetworkIndicators = () => {
    return [
        {
            indicatorDenomination: P106_3.i18n,
            indicator: P106_3.name,
            code: P106_3.code,
            unit: i18n.m3KmD,
        },
        {
            indicatorDenomination: P104_3.i18n,
            indicator: P104_3.name,
            code: P104_3.code,
            unit: '%',
        },
        {
            indicatorDenomination: P105_3.i18n,
            indicator: P105_3.name,
            code: P105_3.code,
            unit: 'm³',
        },
        {
            indicatorDenomination: P107_2.i18n,
            indicator: P107_2.name,
            code: P107_2.code,
            unit: '%',
        },
        {
            indicatorDenomination: P103_2B.i18n,
            indicator: P103_2B.name,
            code: P103_2B.code,
            unit: i18n.ratingUnit,
        },
    ]
}

export const getQualityIndicators = () => {
    return [
        {
            indicatorDenomination: P102_1.i18n,
            indicator: P102_1.name,
            code: P102_1.code,
            unit: '%',
        },
        {
            indicatorDenomination: P101_1.i18n,
            indicator: P101_1.name,
            code: P101_1.code,
            unit: '%',
        },
        {
            indicatorDenomination: P108_3.i18n,
            indicator: P108_3.name,
            code: P108_3.code,
            unit: '%',
        },
    ]
}

export const getFinancialIndicators = () => {
    return [
        {
            indicatorDenomination: P153_2.i18n,
            indicator: P153_2.name,
            code: P153_2.code,
            unit: i18n.year,
        },
        {
            indicatorDenomination: P154_0.i18n,
            indicator: P154_0.name,
            code: P154_0.code,
            unit: '%',
        },
        {
            indicatorDenomination: P109_0.i18n,
            indicator: P109_0.name,
            code: P109_0.code,
            unit: '€/m³',
        },
    ]
}

export const getIndicatorDialogRow = ({
    readMode=false,
    codeIndicator='',
    indicatorName='',
    unit='',
    entityDialog={},
    rpqs={},
    onChange=() => {},
    keyLabel='',
    resumption='',
    inputType='',
}) => {
    const year = rpqs.year
    const previousYear = year-1
    const currentEntity = entityDialog?.[year]
    const previousEntity = entityDialog?.[previousYear]

    const codeKey = keyLabel || codeIndicator

    return {
        unit,
        resumption,
        inputType,
        indicator: codeIndicator,
        indicatorDenomination: indicatorName,
        [previousYear]: previousEntity?.[codeKey]?.value ? previousEntity?.[codeKey]?.value : 0,
        [year]:
            {
                readMode,
                value: currentEntity?.[codeKey]?.value ? currentEntity?.[codeKey]?.value : 0,
                onChange,
            },
    }
}

export const getZoneDialogRow = ({
    readMode=false,
    codeIndicator='',
    indicatorName='',
    unit='',
    onChange=() => {},
    keyLabel='',
    zone={},
    currentYear='',
}) => {
    const codeKey = keyLabel || codeIndicator
    const previousZone = zone?.[currentYear-1]
    const currentZone = zone?.[currentYear]

    return {
        unit,
        indicator: codeIndicator,
        indicatorDenomination: indicatorName,
        [currentYear-1]: previousZone?.[codeKey] ? previousZone?.[codeKey] : 0,
        [currentYear]:
            {
                readMode,
                value: currentZone?.[codeKey] ? currentZone?.[codeKey] : 0,
                onChange,
            },
    }
}

export const updateIndicatorEntity = (entity, year, indicatorCode, newIndicator) => {
    if (!newIndicator) {
        const entityRemovedKey = omit(entity[year], indicatorCode)
        return { ...entity, [year]: { ...entityRemovedKey } }
    }

    return {
        ...entity,
        [year]: {
            ...entity[year],
            [indicatorCode]: newIndicator,
        },
    }
}

export const updateManyIndicatorEntity = (entity, year, indicators) => {
    if (!indicators?.length) return entity

    const formatedIndicators = fromPairs(indicators.map(indi => [indi.code, indi.value]))
    return { ...entity, [year]: { ...entity[year], ...formatedIndicators } }
}